import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import Logo from "@/components/Logo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", organization_id: "" });
  const [openOrg, setOpenOrg] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/organizations").then(r => setOrgs(r.data)); }, []);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization_id) { toast.error("Välj din Arbetarekommun"); return; }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Välkommen, ${u.first_name}!`);
      nav("/hem", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registrering misslyckades");
    } finally { setLoading(false); }
  };

  const selectedOrg = orgs.find(o => o.id === form.organization_id);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-10 px-5">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8">
        <div className="mb-6">
          <Logo variant="inline" size="md"/>
        </div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-1">Skapa konto</h1>
        <p className="text-sm text-slate-500 mb-6">Inget lösenord behövs. Du loggas in direkt.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Förnamn</Label>
              <Input required data-testid="reg-first" value={form.first_name} onChange={e=>set("first_name", e.target.value)} className="h-11"/>
            </div>
            <div className="space-y-2">
              <Label>Efternamn</Label>
              <Input required data-testid="reg-last" value={form.last_name} onChange={e=>set("last_name", e.target.value)} className="h-11"/>
            </div>
          </div>
          <div className="space-y-2">
            <Label>E-postadress</Label>
            <Input required type="email" data-testid="reg-email" value={form.email} onChange={e=>set("email", e.target.value)} className="h-11"/>
          </div>
          <div className="space-y-2">
            <Label>Mobilnummer (valfritt)</Label>
            <Input data-testid="reg-phone" value={form.phone} onChange={e=>set("phone", e.target.value)} className="h-11"/>
          </div>
          <div className="space-y-2">
            <Label>Arbetarekommun</Label>
            <Popover open={openOrg} onOpenChange={setOpenOrg}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" data-testid="reg-org-trigger"
                  className={cn("w-full justify-between h-11 font-normal", !form.organization_id && "text-slate-400")}>
                  {selectedOrg ? selectedOrg.name : "Välj Arbetarekommun…"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Sök kommun…" data-testid="reg-org-search"/>
                  <CommandList>
                    <CommandEmpty>Ingen träff.</CommandEmpty>
                    <CommandGroup>
                      {orgs.map(o => (
                        <CommandItem key={o.id} value={o.name} data-testid={`reg-org-${o.name}`}
                          onSelect={() => { set("organization_id", o.id); setOpenOrg(false); }}>
                          <Check className={cn("mr-2 h-4 w-4", form.organization_id === o.id ? "opacity-100" : "opacity-0")}/>
                          {o.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <Button type="submit" data-testid="reg-submit" disabled={loading}
            className="w-full h-12 bg-[#E1251B] hover:bg-[#C81D14] text-white text-base font-semibold rounded-full mt-2">
            {loading ? "Skapar konto…" : "Skapa konto & logga in"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-slate-600 text-center">
          Har du redan konto? <Link to="/login" data-testid="reg-goto-login" className="font-semibold text-[#E1251B] hover:underline">Logga in</Link>
        </p>
      </div>
    </div>
  );
}
